/**
 * Australia Post tracking number validation and formatting utilities
 */

export interface TrackingValidationResult {
  isValid: boolean;
  format: string;
  formatted: string;
  carrier: string;
  description: string;
}

// Australia Post tracking number patterns
const AUS_POST_PATTERNS = [
  {
    pattern: /^(\d{13})$/,
    format: 'Standard 13-digit',
    description: 'Australia Post standard tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  },
  {
    pattern: /^(7318290\d{15})$/,
    format: 'eParcel 22-digit',
    description: 'Australia Post eParcel tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  },
  {
    pattern: /^([A-Z]{2}\d{9}[A-Z]{2})$/,
    format: 'International format',
    description: 'Australia Post international tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  },
  {
    pattern: /^(EP\d{9}AU)$/,
    format: 'Express Post',
    description: 'Australia Post Express Post tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  },
  {
    pattern: /^(RR\d{9}AU)$/,
    format: 'Registered Post',
    description: 'Australia Post Registered Post tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  },
  {
    pattern: /^(CP\d{9}AU)$/,
    format: 'Parcel Post',
    description: 'Australia Post Parcel Post tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  },
  {
    pattern: /^(EE\d{9}AU)$/,
    format: 'Express Courier',
    description: 'Australia Post Express Courier tracking number',
    formatter: (match: RegExpMatchArray) => match[1]
  }
];

// Other common carriers for reference
const OTHER_CARRIER_PATTERNS = [
  {
    pattern: /^(1Z[0-9A-Z]{16})$/,
    format: 'UPS',
    description: 'UPS tracking number',
    carrier: 'UPS'
  },
  {
    pattern: /^(\d{12})$/,
    format: 'FedEx 12-digit',
    description: 'FedEx tracking number',
    carrier: 'FedEx'
  },
  {
    pattern: /^(\d{14})$/,
    format: 'FedEx 14-digit',
    description: 'FedEx tracking number',
    carrier: 'FedEx'
  }
];

/**
 * Validates and formats an Australia Post tracking number
 */
export function validateAustraliaPostTracking(input: string): TrackingValidationResult {
  if (!input) {
    return {
      isValid: false,
      format: 'Unknown',
      formatted: '',
      carrier: 'Unknown',
      description: 'No tracking number provided'
    };
  }

  // Clean the input - remove spaces, dashes, and convert to uppercase
  const cleaned = input.replace(/[\s\-]/g, '').toUpperCase();

  // Check Australia Post patterns
  for (const patternInfo of AUS_POST_PATTERNS) {
    const match = cleaned.match(patternInfo.pattern);
    if (match) {
      return {
        isValid: true,
        format: patternInfo.format,
        formatted: patternInfo.formatter(match),
        carrier: 'Australia Post',
        description: patternInfo.description
      };
    }
  }

  // Check other carriers
  for (const patternInfo of OTHER_CARRIER_PATTERNS) {
    const match = cleaned.match(patternInfo.pattern);
    if (match) {
      return {
        isValid: true,
        format: patternInfo.format,
        formatted: match[1],
        carrier: patternInfo.carrier || 'Other',
        description: patternInfo.description
      };
    }
  }

  return {
    isValid: false,
    format: 'Unknown',
    formatted: cleaned,
    carrier: 'Unknown',
    description: 'Tracking number format not recognized'
  };
}

/**
 * Quick validation for Australia Post tracking numbers only
 */
export function isValidAustraliaPostTracking(input: string): boolean {
  const result = validateAustraliaPostTracking(input);
  return result.isValid && result.carrier === 'Australia Post';
}

/**
 * Format tracking number for display
 */
export function formatTrackingNumber(input: string): string {
  const result = validateAustraliaPostTracking(input);
  return result.formatted || input;
}

/**
 * Get tracking number suggestions based on partial input
 */
export function getTrackingSuggestions(input: string): string[] {
  const cleaned = input.replace(/[\s\-]/g, '').toUpperCase();
  const suggestions: string[] = [];

  if (cleaned.length >= 2) {
    // Suggest common Australia Post prefixes
    const prefixes = ['EP', 'RR', 'CP', 'EE'];
    for (const prefix of prefixes) {
      if (prefix.startsWith(cleaned) || cleaned.startsWith(prefix)) {
        suggestions.push(`${prefix}XXXXXXXXX AU (${prefix === 'EP' ? 'Express Post' : prefix === 'RR' ? 'Registered Post' : prefix === 'CP' ? 'Parcel Post' : 'Express Courier'})`);
      }
    }
  }

  if (cleaned.length >= 4 && cleaned.startsWith('7318290')) {
    suggestions.push('7318290XXXXXXXXXXXXXXX (eParcel 22-digit)');
  }

  if (cleaned.length >= 1 && /^\d+$/.test(cleaned)) {
    suggestions.push('XXXXXXXXXXXXX (13-digit standard)');
  }

  return suggestions.slice(0, 3); // Limit to 3 suggestions
}